/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2024.
 */

import {
	unitFreePriceSX,
	unitPriceSX,
} from '@/components/blocks/UnitPriceDisplay/style/unitPriceDisplay';
import { orderItemTablePriceSX } from '@/components/content/OrderItemTable/styles/orderItemTablePrice';
import { useCurrencyFormat } from '@/data/Content/CurrencyFormat';
import { useLocalization } from '@/data/Localization';
import { dFix, useSettings } from '@/data/Settings';
import { ProductDisplayPrice } from '@/data/types/Product';
import { formatPrice } from '@/utils/formatPrice';
import { Stack, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { LOCALE_ES_MX } from 'shared/custom/constants/commonConstants';

type Props = {
	unitPrice: string;
	unitPriceCurrency: string;
	prices: ProductDisplayPrice;
};

export const UnitPriceDisplay: FC<Props> = ({ unitPrice, unitPriceCurrency, prices }) => {
	const { offer = 0, list = 0, currency: _currency } = prices;
	const { settings } = useSettings();
	const { decimalPlaces } = useCurrencyFormat();
	const localization = useLocalization('PriceDisplay');
	const currency = useMemo(() => _currency ?? settings?.defaultCurrency, [_currency, settings]);
	const unitPriceFromItem = dFix(unitPrice);

	return (
		<Stack direction="row" spacing={0.5} alignItems="end">
			{unitPrice?.trim() ? (
				<Typography
					data-testid="offer-price"
					id="offer-price"
					variant="bodySubText"
					sx={unitPriceFromItem === 0 ? unitFreePriceSX : unitPriceSX}
				>
					{unitPriceFromItem === 0
						? localization.Labels.Free.t()
						: localization.Labels.UnitPrice.t({
								price: formatPrice(
									LOCALE_ES_MX,
									unitPriceCurrency,
									unitPriceFromItem,
									decimalPlaces
								),
						  })}
				</Typography>
			) : (
				<>
					{offer > 0 && offer < list ? (
						<Typography sx={orderItemTablePriceSX} data-testid="list-price" id="list-price">
							{formatPrice(LOCALE_ES_MX, currency, list, decimalPlaces)}
						</Typography>
					) : null}

					{offer > 0 ? (
						<Typography data-testid="offer-price" id="offer-price">
							{localization.Labels.PerUnit.t({
								price: formatPrice(LOCALE_ES_MX, currency, offer, decimalPlaces),
							})}
						</Typography>
					) : null}

					{offer === 0 ? (
						<Typography data-testid="no-price" id="no-price">
							{localization.Labels.Pending.t()}
						</Typography>
					) : null}
				</>
			)}
		</Stack>
	);
};
